/**
 * Detección de contenido peligroso en los textos con formato (RF-012).
 *
 * El renderizador ya neutraliza lo que llega a la pantalla, pero eso ocurre **al mostrar**. Un
 * archivo importado con `<script>` dentro se guardaría tal cual en la base de datos, y a partir
 * de ahí basta con que un día alguien lo lea sin pasar por el renderizador —un informe, una
 * exportación a otro formato, un panel nuevo— para que el peligro salga. Se rechaza al entrar.
 *
 * Esto **no es un sanitizador**: no limpia nada ni sustituye al saneado del renderizador, que
 * sigue siendo la defensa que importa. Es una comprobación de entrada, deliberadamente
 * conservadora, para que lo que se almacena sea texto con formato y no marcado activo.
 *
 * **Lo que va dentro de código no cuenta.** Un enunciado de informática con ```` ```html ```` y un
 * `<script>` dentro es contenido legítimo, y el renderizador lo escapa: rechazarlo dejaría fuera
 * justo al profesorado que más lo necesita. Por eso el texto se examina con los bloques y los
 * fragmentos de código ya retirados.
 */
/**
 * Etiquetas que no tienen ningún uso en un enunciado y sí muchos en un ataque.
 *
 * La lista es la misma que el renderizador prohíbe, más las que ni siquiera llegan a él porque
 * Markdown no las produce nunca.
 */
const FORBIDDEN_TAGS = [
    'script',
    'iframe',
    'object',
    'embed',
    'style',
    'svg',
    'math',
    'form',
    'input',
    'button',
    'link',
    'meta',
    'base',
    'img',
];
/** Esquemas de URL que ejecutan en lugar de navegar. */
const DANGEROUS_SCHEMES = ['javascript', 'vbscript', 'data'];
const TAG_PATTERN = new RegExp(`<\\s*/?\\s*(?:${FORBIDDEN_TAGS.join('|')})\\b`, 'i');
/**
 * Un atributo de evento dentro de una etiqueta: `onerror=`, `onclick=`, `onload=`…
 *
 * Se exige que vaya precedido de un carácter de separación y seguido de `=` para no marcar la
 * palabra «online» de un enunciado corriente.
 */
const EVENT_ATTR_PATTERN = /<[^>]*\son[a-z]+\s*=/i;
/**
 * Un esquema peligroso donde se espera una URL.
 *
 * Cubre el destino de un enlace de Markdown —`[texto](javascript:…)`—, el de una referencia
 * —`[texto]: javascript:…`— y el de un `href`/`src` en HTML crudo. No basta con buscar
 * `javascript:` suelto: escrito en un enunciado sobre programación es una palabra, no un enlace.
 */
const DANGEROUS_URL_PATTERN = new RegExp(`(?:\\]\\s*\\(|\\]\\s*:\\s*|\\b(?:href|src|xlink:href)\\s*=\\s*["']?)\\s*` +
    `(?:${DANGEROUS_SCHEMES.join('|')})\\s*:`, 'i');
/**
 * Quita de la vista lo que el renderizador va a escapar de todas formas.
 *
 * Se sustituye por espacios en vez de borrarse para no pegar dos trozos que no estaban juntos y
 * fabricar así una coincidencia que el texto original no tenía.
 */
function withoutCode(text) {
    const blank = (match) => ' '.repeat(match.length);
    return text
        .replace(/```[\s\S]*?```/g, blank) // bloque vallado
        .replace(/~~~[\s\S]*?~~~/g, blank) // bloque vallado, la otra forma
        .replace(/`[^`\n]*`/g, blank) // fragmento en línea
        .replace(/^(?: {4}|\t).*$/gm, blank); // bloque indentado
}
/**
 * Qué hay de peligroso en el texto, o `null` si no hay nada.
 *
 * Devuelve la clase de problema y no solo un booleano porque el aviso al profesor cambia: no se
 * arregla igual una etiqueta pegada por error que un enlace que ejecuta.
 */
export function findDangerousMarkup(text) {
    const visible = withoutCode(text);
    if (TAG_PATTERN.test(visible))
        return 'tag';
    if (EVENT_ATTR_PATTERN.test(visible))
        return 'eventAttribute';
    if (DANGEROUS_URL_PATTERN.test(visible))
        return 'url';
    return null;
}
//# sourceMappingURL=dangerous-markup.js.map